import Phaser from 'phaser';
import { getAutoCreateCode, getAutoJoinCode } from '../utils/DebugConfig';

/**
 * MenuScene - Main menu with play button (fully responsive)
 */
export class MenuScene extends Phaser.Scene {
    // Store UI element references for repositioning on resize
    private title!: Phaser.GameObjects.Text;
    private subtitle!: Phaser.GameObjects.Text;
    private practiceButton!: { button: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
    private multiplayerButton!: { button: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text };
    private instructions!: Phaser.GameObjects.Text;
    private version!: Phaser.GameObjects.Text;
    private titleTween!: Phaser.Tweens.Tween;

    constructor() {
        super({ key: 'MenuScene' });
    }

    async create(): Promise<void> {
        // Title
        this.title = this.add.text(0, 0, '💨 FART BATTLE 💨', {
            fontFamily: 'Arial Black, Arial',
            fontSize: '48px',
            color: '#4ecdc4',
            stroke: '#2a9d8f',
            strokeThickness: 6,
        });
        this.title.setOrigin(0.5);

        // Subtitle
        this.subtitle = this.add.text(0, 0, 'ROYALE', {
            fontFamily: 'Arial Black, Arial',
            fontSize: '36px',
            color: '#fdcb6e',
            stroke: '#e17055',
            strokeThickness: 4,
        });
        this.subtitle.setOrigin(0.5);

        // Single Player button
        this.practiceButton = this.createButton(0, 0, 'PRACTICE', 0x00b894, 0x00886a, () => {
            this.startGame();
        });

        // Multiplayer button
        this.multiplayerButton = this.createButton(0, 0, 'MULTIPLAYER', 0x6c5ce7, 0x5849c4, () => {
            this.goToLobby();
        });

        // Instructions
        this.instructions = this.add.text(0, 0,
            'WASD or Arrow Keys to move | Space to fart', {
            fontFamily: 'Arial',
            fontSize: '14px',
            color: '#666666',
            align: 'center',
        });
        this.instructions.setOrigin(0.5);

        // Version - import dynamically from package.json
        const pkg = await import('../../package.json');
        this.version = this.add.text(0, 0, `v${pkg.version}`, {
            fontFamily: 'Arial',
            fontSize: '12px',
            color: '#555555',
        });
        this.version.setOrigin(1, 1);

        // Position all elements based on current viewport
        this.layoutUI();

        // Listen for resize events
        this.scale.on('resize', this.handleResize, this);

        // Add floating animation to title
        this.startTitleAnimation();

        // Check for debug auto-actions
        this.time.delayedCall(50, () => {
            if (getAutoCreateCode() || getAutoJoinCode()) {
                this.goToLobby();
            }
        });
    }

    /**
     * Position all UI elements based on current viewport size
     */
    private layoutUI(): void {
        // Guard against being called before camera is ready
        if (!this.cameras?.main) return;

        const { width, height } = this.cameras.main;
        const centerX = width / 2;
        const centerY = height / 2;

        // Scale font sizes for smaller screens
        const isMobile = width < 500;
        const titleSize = isMobile ? '32px' : '48px';
        const subtitleSize = isMobile ? '24px' : '36px';
        const buttonWidth = isMobile ? 180 : 220;
        const buttonHeight = isMobile ? 44 : 50;
        const buttonFontSize = isMobile ? '18px' : '22px';

        // Update title
        this.title.setPosition(centerX, centerY - (isMobile ? 90 : 120));
        this.title.setFontSize(titleSize);

        // Update subtitle
        this.subtitle.setPosition(centerX, centerY - (isMobile ? 50 : 60));
        this.subtitle.setFontSize(subtitleSize);

        // Update practice button
        this.practiceButton.button.setPosition(centerX, centerY + (isMobile ? 10 : 20));
        this.practiceButton.button.setSize(buttonWidth, buttonHeight);
        this.practiceButton.label.setPosition(centerX, centerY + (isMobile ? 10 : 20));
        this.practiceButton.label.setFontSize(buttonFontSize);

        // Update multiplayer button
        this.multiplayerButton.button.setPosition(centerX, centerY + (isMobile ? 65 : 90));
        this.multiplayerButton.button.setSize(buttonWidth, buttonHeight);
        this.multiplayerButton.label.setPosition(centerX, centerY + (isMobile ? 65 : 90));
        this.multiplayerButton.label.setFontSize(buttonFontSize);

        // Update instructions
        this.instructions.setPosition(centerX, centerY + (isMobile ? 120 : 170));
        this.instructions.setFontSize(isMobile ? '12px' : '14px');

        // Update version (bottom-right corner)
        this.version.setPosition(width - 10, height - 10);
    }

    /**
     * Handle window resize
     */
    private handleResize(): void {
        // Stop current tween to get base positions
        if (this.titleTween) {
            this.titleTween.stop();
        }

        // Reposition all elements
        this.layoutUI();

        // Restart the animation with new positions
        this.startTitleAnimation();
    }

    /**
     * Start the floating animation for title and subtitle
     */
    private startTitleAnimation(): void {
        this.titleTween = this.tweens.add({
            targets: [this.title, this.subtitle],
            y: '+=10',
            duration: 1500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
        });
    }

    private createButton(
        x: number, y: number, text: string,
        fillColor: number, strokeColor: number,
        onClick: () => void
    ): { button: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text } {
        const button = this.add.rectangle(x, y, 220, 50, fillColor);
        button.setInteractive({ useHandCursor: true });
        button.setStrokeStyle(3, strokeColor);

        const label = this.add.text(x, y, text, {
            fontFamily: 'Arial Black, Arial',
            fontSize: '22px',
            color: '#ffffff',
        });
        label.setOrigin(0.5);

        button.on('pointerover', () => {
            button.setScale(1.05);
            label.setScale(1.05);
        });
        button.on('pointerout', () => {
            button.setScale(1);
            label.setScale(1);
        });
        button.on('pointerup', onClick);

        return { button, label };
    }

    private startGame(): void {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.start('GameScene');
        });
    }

    private goToLobby(): void {
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
            this.scene.start('LobbyScene');
        });
    }
}
